// ============================================================================
// Finance handoff — the approved list Jose works from (status verified or
// sent_to_finance). Export to CSV for the QBO bill run. qbo_bill_id is surfaced
// but the QBO write is intentionally NOT built here (Jose approves all QBO write
// operations).
//
// CW-Q 4.4: this page used to be entirely read-only, so the one thing finance
// actually does here — mark an approved payout paid — was not reachable from it.
// Jose's route was: find the row on this list, go to Payouts, filter, find it
// again, and only then act; and for a `verified` No-Miss there was no action at
// all, because `actionsFor` only ever offered that state's buttons to an
// APPROVER. Now the action sits on the row he is already looking at, one click
// from landing. No DB or policy change: the role x status gate has always
// permitted finance `verified|sent_to_finance -> paid`, proved by probe inside a
// rolled-back transaction on 2026-08-14.
// ============================================================================

import { useState } from 'react';
import { ErrorState } from '../../shared/components';
import { fmtCurrency } from '../../shared/format';
import { useAuth } from '../../shared/auth';
import { errorMessage } from '../../shared/errors';
import {
  setPayoutStatus,
  useFinanceHandoff,
  type FinanceHandoffRow,
} from './hooks/use-incentive-data';
import { actionsFor, capsForRole } from './payout-actions';
import { formatPeriod, payoutTypeShort } from './format';

function toCsv(rows: FinanceHandoffRow[]): string {
  const head = [
    'period', 'payout_type', 'status', 'facility', 'account', 'market',
    'sub', 'worker', 'recipient_type', 'amount', 'verified_at',
    'routed_to_finance_at', 'qbo_bill_id', 'override_justification',
  ];
  const esc = (v: unknown) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = rows.map((r) =>
    [
      r.period, r.payout_type, r.status, r.facility_name, r.account_name,
      r.market_region, r.provider_name, r.worker_name, r.recipient_type,
      Number(r.amount || 0).toFixed(2), r.verified_at, r.routed_to_finance_at,
      r.qbo_bill_id, r.override_justification,
    ]
      .map(esc)
      .join(','),
  );
  return [head.join(','), ...lines].join('\n');
}

function downloadCsv(rows: FinanceHandoffRow[]) {
  const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `incentive-finance-handoff-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function FinanceHandoffView() {
  const { rows, isLoading, error, refetch } = useFinanceHandoff();
  const { profile } = useAuth();
  const caps = capsForRole(profile?.role);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  async function markPaid(r: FinanceHandoffRow) {
    setBusyId(r.payout_id);
    setRowError(null);
    try {
      await setPayoutStatus(r.payout_id, 'paid');
      refetch();
    } catch (e) {
      setRowError(errorMessage(e, 'Could not mark it paid.', 'incentive'));
    } finally {
      setBusyId(null);
    }
  }

  if (error) return <ErrorState body={error} onRetry={refetch} />;
  if (isLoading)
    return <p className="text-sm text-tcs-text-muted">Loading handoff…</p>;

  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-tcs-text-muted">
          Approved payouts awaiting payment (verified + sent to finance).{' '}
          <span className="font-semibold text-tcs-text">
            {rows.length} row{rows.length === 1 ? '' : 's'} ·{' '}
            {fmtCurrency(total)}
          </span>
        </p>
        <button
          type="button"
          onClick={() => downloadCsv(rows)}
          disabled={rows.length === 0}
          className="rounded-md border border-tcs-border bg-tcs-raised px-3 py-1.5 text-xs font-medium text-tcs-text hover:bg-tcs-surface disabled:opacity-60"
        >
          Download CSV
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-tcs-border bg-tcs-surface p-4 text-sm text-tcs-text-muted">
          Nothing approved for payment right now.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-tcs-border bg-tcs-bg">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-tcs-text-muted">
                <th className="px-3 py-2 font-semibold">Period</th>
                <th className="px-3 py-2 font-semibold">Facility (sub)</th>
                <th className="px-3 py-2 font-semibold">Type</th>
                <th className="px-3 py-2 font-semibold">Amount</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">
                  <span className="sr-only">Action</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.payout_id} className="border-t border-tcs-border">
                  <td className="px-3 py-2 text-tcs-text">
                    {formatPeriod(r.period)}
                  </td>
                  <td className="px-3 py-2 text-tcs-text">
                    {r.facility_name ?? '—'}{' '}
                    <span className="text-tcs-text-secondary">
                      ({r.provider_name ?? r.worker_name ?? '—'})
                    </span>
                  </td>
                  <td className="px-3 py-2 text-tcs-text-secondary">
                    {payoutTypeShort(r.payout_type)}
                  </td>
                  <td className="px-3 py-2 font-semibold text-tcs-text">
                    {fmtCurrency(Number(r.amount || 0))}
                  </td>
                  <td className="px-3 py-2 text-tcs-text-secondary">
                    {r.status === 'sent_to_finance' ? 'Sent to finance' : 'Verified'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {actionsFor(r.status, r.payout_type, caps).some(
                      (a) => a.kind === 'mark_paid' || a.kind === 'mark_given',
                    ) ? (
                      <button
                        type="button"
                        onClick={() => markPaid(r)}
                        disabled={busyId === r.payout_id}
                        className="rounded-md bg-tcs-inverse px-3 py-1 text-xs font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-60"
                      >
                        {busyId === r.payout_id ? 'Marking…' : 'Mark paid'}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rowError && (
        <p className="mt-2 text-xs text-st-danger-fg">{rowError}</p>
      )}
    </div>
  );
}
